<template>
  <div class="fts-full-calendar">
    <div class="fc-header">
      <div class="fc-header-l">
        <div class="fc-header-title">{{title}}</div>
        <div class="fc-header-content" v-if="!!$slots.headerContent">
          <slot name="headerContent" />
        </div>
      </div>

      <div class="fc-header-r">
        <fts-btn outline sm @click="prev">Prev</fts-btn>
        <fts-btn outline sm @click="next">Next</fts-btn>
        <fts-btn outline sm @click="today">Today</fts-btn>
      </div>
    </div>

    <full-calendar ref="fullCalendar" :options="calendarOptions">
      <template #eventContent="{timeText, event}">
        <slot name="eventContent" :timeText="timeText" :event="event" contentClass="fc-event-content" titleClass="fc-event-content-title">
          <div class="fc-event-content">
            <div class="fc-event-content-title">{{ event.title }}</div>
          </div>
        </slot>
      </template> 
    </full-calendar>
  </div>
</template>

<script>
import commons from "../commons/commons";

import '@fullcalendar/core/vdom';
import FullCalendar from "@fullcalendar/vue";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import moment from "moment";

import FTSBtn from "./FTSBtn";

export default {
  components: {
    FullCalendar,
    "fts-btn": FTSBtn
  },
  props: {
    events: {
      type: Array,
      default: []
    },
  },
  data: function () {
    return {
      calendarOptions: {
        plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
        headerToolbar: false,
        // headerToolbar: {
        //   left: "title",
        //   center: "title",
        //   right: "prev,next today" // "dayGridMonth,timeGridWeek,timeGridDay"
        // },
        initialView: "dayGridMonth",
        editable: false,
        selectable: true,
        selectMirror: true,
        dayMaxEvents: true,
        weekends: true,
        eventClick: this.handleEventClick,
        events: [],
      },
      fullCalendarApi: {},
      title: ""
    }
  },
  methods: {
    toggleWeekends() {
      this.calendarOptions.weekends = !this.calendarOptions.weekends
    },
    handleEventClick(x) {
      /* workaround close popover */
      try {
        const el = this.$refs.fullCalendar.$el.getElementsByClassName('fc-popover')[0];
        el && el.remove();
      } catch(err) {
        console.warn("[DOMException]", err);
      }

      this.$emit('eventClick', {
        id: x.event.id,
        title: x.event.title,
        ...x.event.extendedProps
      })
    },
    today() {
      this.fullCalendarApi.today()
      this.title = this.fullCalendarApi.view.title
    },
    prev() {
      this.fullCalendarApi.prev()
      this.title = this.fullCalendarApi.view.title
      this.$emit('prev')
    },
    next() {   
      this.fullCalendarApi.next()
      this.title = this.fullCalendarApi.view.title
      this.$emit('next')
    },
  },
  computed: {
    formattedEvents() {
      return this.events.reduce((events, x) => {
        if (x.start) {
          events.push({
            id: x.id,
            title: x.title,
            start: moment(x.start).format('YYYY-MM-DD'),
            end: moment(x.end && commons.validDate(x.end) ? x.end : x.start).add(1, 'days').format('YYYY-MM-DD'),
            color: x.color,
            payload: x.payload
          })
        }
        return events
      }, [])
    }
  },
  watch: {
    events: {
      deep: true,
      handler() {
        this.calendarOptions.events = this.formattedEvents;
      }
    }
  },
  mounted() {
    if (this.events)
      this.calendarOptions.events = this.formattedEvents;

    this.$nextTick(() => {
      this.fullCalendarApi = this.$refs.fullCalendar.getApi();
      this.title = this.fullCalendarApi.view.title;
    })
  },
}
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

$minWidth: 768px;

.fts-full-calendar {
  .fc-header {
    min-width: $minWidth;
    display: flex;
    align-items: center;
    margin-bottom: .75rem;
    .fc-header-l {
      flex: 1;
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      margin-top: -0.75rem;
      margin-left: -1rem;
      .fc-header-title {
        margin-top: .75rem;
        margin-left: 1rem;
        white-space: nowrap;
        font-size: 1.5rem;
        font-weight: 500;
        color: var(--fts-text);
      }
      .fc-header-content {
        margin-top: .75rem;
        margin-left: 1rem;
      }
    }
    .fc-header-r {
      flex-shrink: 0;
      text-align: right;
    }
  }
  .fc {
    min-width: $minWidth;
    background: var(--fts-background);
    .fc-daygrid-event {
      background: $primaryColor;
      border-color: $primaryColor;
      cursor: pointer;
      .fc-event-content {
        display: flex;
        padding: .375rem;
        .fc-event-content-title {
          @include text-overflow-ellipsis;
        }
      }
    }
  }
}

.fc {
  .fc-toolbar {
    &.fc-header-toolbar {
      margin-bottom: 0;
      padding: .375rem;
      @include theme() {
        background: rgba($color: theme-get('fts-background'), $alpha: .95);
      }
      .fc-toolbar-title {
        padding: .175rem .75rem;
        line-height: 1.5;
        font-weight: 500;
      }
      .fc-button {
        border: 0;
        background: none;
        color: var(--fts-gray-5);
        &.fc-today-button {
          margin-left: 0;
        }
        .fc-icon {
          font-size: 1rem;
        }
        &:focus, &:active {
          box-shadow: none;
        }
        &:disabled {
          cursor: not-allowed;
        }
      }
    }
  }
  .fc-view-harness {
    @include theme() {
      background: rgba($color: theme-get('fts-background'), $alpha: .95);
    }
    color: var(--fts-text);
    .fc-col-header  {
      .fc-col-header-cell {
        .fc-col-header-cell-cushion {
          font-size: .75rem;
        }
      }
    }
    .fc-scrollgrid {
      border-color: var(--fts-gray-3);
      tr {
        > th,
        > td {
          border-color: var(--fts-gray-3);
        }
      }
    }
    .fc-popover {
      border-color: var(--fts-gray-3);
      background: var(--fts-background);
      .fc-popover-header {
        background: var(--fts-gray-2);
      }
    }
    .fc-daygrid-day {
      .fc-daygrid-day-top {
        justify-content: center;
        padding-top: .375rem;
        padding-bottom: .375rem;
        > a {
          @include flex-center-middle;
          height: 30px;
          width: 30px;
          overflow: hidden;
          border-radius: 50%;
          font-size: .75rem;
        }
      }
      .fc-daygrid-day-events {
        margin-top: 0;
        .fc-daygrid-event {
          margin-top: .135rem;
        }
      }
       &.fc-day-today {
        background: transparent;
        .fc-daygrid-day-top {
          > a {
            border: 1px solid $primaryColor;
            background: $primaryColor;
            color: #fff;
          }
        }
      }
    }
  }
  .fc-highlight {
    background: rgba($color: $primaryColor, $alpha: 0.095);
  }
}
</style>